import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

// Function to scroll to top
const scrollToTop = () => {
  window.scrollTo(0, 0);
};

// Function to scroll to specific section
const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerHeight = 80; // Approximate header height
    const elementPosition = element.offsetTop - headerHeight - 20; // Extra 20px padding
    window.scrollTo({
      top: elementPosition,
      behavior: 'smooth'
    });
  }
};

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Contact Us', href: '/contact' }
  ];

  const serviceLinks = [
    { name: 'Compliance', href: '/services#compliance-section' },
    { name: 'Review & Audit', href: '/services#review-audit-section' },
    { name: 'Refund Services', href: '/services#refund-services-section' },
    { name: 'Advisory & Consultancy', href: '/services#advisory-section' },
    { name: 'Representation, Appeals & Litigation', href: '/services#representation-section' }
  ];

  const socialLinks = [
    { name: 'Facebook', href: '#', icon: Facebook },
    { name: 'Twitter', href: '#', icon: Twitter },
    { name: 'LinkedIn', href: '#', icon: Linkedin },
    { name: 'Instagram', href: '#', icon: Instagram }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">TaxYukti</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Your trusted partner for comprehensive GST solutions. We provide expert guidance, 
                compliance support, and strategic advisory to help your business thrive.
              </p>
            </div>
            
            {/* Social Links - Hidden for now */}
            {/* <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  aria-label={social.name}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div> */}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Our Services</h4>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => {
                      // Navigate to services page first, then scroll to section
                      navigate('/services');
                      // Use setTimeout to ensure navigation completes before scrolling
                      setTimeout(() => {
                        const sectionId = link.href.split('#')[1];
                        if (sectionId) {
                          scrollToSection(sectionId);
                        }
                      }, 100);
                    }}
                    className="text-gray-300 hover:text-white transition-colors duration-200 text-sm text-left bg-transparent border-none cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Contact Info</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <MapPin className="text-slate-400 mt-1 mr-3 flex-shrink-0" size={16} />
                <span className="text-gray-300">
                  Bengaluru, Karnataka<br />
                  India
                </span>
              </div>
              <div className="flex items-center">
                <Phone className="text-slate-400 mr-3 flex-shrink-0" size={16} />
                <span className="text-gray-300">+91 9480242651</span>
              </div>
              <div className="flex items-center">
                <Phone className="text-slate-400 mr-3 flex-shrink-0" size={16} />
                <span className="text-gray-300">+91 8867992959</span>
              </div>
              <div className="flex items-center">
                <Mail className="text-slate-400 mr-3 flex-shrink-0" size={16} />
                <span className="text-gray-300">connect@taxyukti.com</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-300 text-sm">
              © {currentYear} TaxYukti. All rights reserved.
            </div>
                         <div className="flex space-x-6 mt-4 md:mt-0">
               <span className="text-gray-300 text-sm">
                 Privacy Policy
               </span>
               <span className="text-gray-300 text-sm">
                 Terms of Service
               </span>
             </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
